import { useMemo, useRef, useState } from 'react';
import { useLocation } from 'react-router-dom';
import { queryClient } from 'src/components/queryProvider';
import { Snackbar } from 'src/components/snackbar';
import { PATHS } from 'src/constants/paths';
import { useGetEnvironment } from 'src/hooks';
import { fetchBulkReplaceRequests } from 'src/hooks/api/publication/useGetAssemblyPublicationList';
import { lang, queryKeys } from 'src/utils';

import { IAssemblyPublicationData } from '../modules/AssemblyPublicationTable/AssemblyPublicationTable.interface';

export const useExportAllAssemblies = () => {
  const { data: environment } = useGetEnvironment();
  const location = useLocation();
  const [csvData, setCsvData] = useState<any[][]>([]);
  const isFetchingRef = useRef(false);

  const formattedQuery = useMemo(() => {
    const params = new URLSearchParams(location.search);
    const status = params.get('status') || '';

    return `status=${status}&sort=-modified_at`;
  }, [location.search]);

  const fetchAllData = async (): Promise<IAssemblyPublicationData[]> => {
    let page = 1;
    let allData: IAssemblyPublicationData[] = [];
    let hasMore = true;

    while (hasMore) {
      const response = await fetchBulkReplaceRequests(
        page,
        [formattedQuery],
        ['fromComponent', 'toComponent', 'assemblies'],
        true,
      );

      if (response?.data?.length) {
        allData = [...allData, ...response.data];
        page++;
      } else {
        hasMore = false;
      }
    }

    return allData;
  };

  const fetchExportData = async () => {
    if (isFetchingRef.current) return;
    isFetchingRef.current = true;

    try {
      const TableData = await queryClient.fetchQuery({
        queryKey: queryKeys.bulkReplaceRequests.all(formattedQuery),
        queryFn: fetchAllData,
        staleTime: 0,
      });

      if (!TableData.length) {
        setCsvData([]);

        return;
      }

      const { mtUI } = environment?.urls || {};
      const result = TableData.flatMap((item) =>
        item.assemblies.map((assembly) => [
          item.display_name,
          item.id,
          assembly.name,
          assembly.id,
          `${mtUI}/cc-ui/#${PATHS.ASSEMBLY_DETAILS(assembly.id)}`,
          item.fromComponent,
          item.fromComponentId,
          item.toComponent,
          item.toComponentId,
          item.user,
          item.userRole,
          item.modified_at,
        ]),
      );

      setCsvData(result);
    } catch (error) {
      Snackbar.error(`${lang.errorFetchingData} ${error}`);
    } finally {
      isFetchingRef.current = false;
    }
  };

  return {
    csvData,
    fetchExportData,
    setCsvData,
  };
};
